import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { confirmAction } from '../../utils/confirmToast';
import { Users, Plus, User, Phone, CalendarDays, MoreVertical, Trash2 } from 'lucide-react';
import Drawer from '../../components/common/Drawer/Drawer';
import api from '../../api/axios';
import styles from './MyFamily.module.css';

const MyFamily = () => {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);

  const [formData, setFormData] = useState({
    name: '',
    relation: 'Spouse',
    age: '',
    phone: '',
    bloodGroup: ''
  });

  useEffect(() => {
    fetchMembers();
  }, []);

  const fetchMembers = async () => {
    try {
      setLoading(true);
      const { data } = await api.get('/family');
      setMembers(data);
      setLoading(false);
    } catch (err) {
      console.error('Failed to fetch family members');
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post('/family', formData);
      setMembers([data, ...members]);
      setFormData({ name: '', relation: 'Spouse', age: '', phone: '', bloodGroup: '' });
      setIsDrawerOpen(false);
      toast.success('Family member added successfully!');
    } catch (err) {
      toast.error('Failed to add family member');
    }
  };

  const handleDelete = async (id) => {
    confirmAction('Are you sure you want to remove this family member?', async () => {
      try {
        await api.delete(`/family/${id}`);
        setMembers(members.filter(m => m._id !== id));
        toast.success('Family member removed successfully!');
      } catch (err) {
        toast.error('Failed to remove family member');
      }
    });
  };

  return (
    <div className={styles.container}>
      <div className={styles.pageHeader}>
        <div>
          <h1>Family Members</h1>
          <p>Manage details of people living in your unit</p>
        </div>
        <button className={styles.addBtn} onClick={() => setIsDrawerOpen(true)}>
          <Plus size={16} /> Add Member
        </button>
      </div>

      <div className={styles.grid}>
        {loading ? <p>Loading members...</p> : members.map(member => (
          <div key={member._id} className={styles.card}>
            <div className={styles.cardHeader}>
              <div className={styles.avatar}>{member.name.split(' ').map(n => n[0]).join('').substring(0, 2)}</div>
              <button className={styles.menuBtn} onClick={() => handleDelete(member._id)} title="Delete Member">
                <Trash2 size={16} />
              </button>
            </div>
            
            <div className={styles.cardBody}>
              <h3>{member.name}</h3>
              <span className={styles.relationBadge}>{member.relation}</span>
            </div>
            
            <div className={styles.cardFooter}>
              <div className={styles.footerItem}>
                <CalendarDays size={14} className={styles.icon} />
                <span>{member.age} yrs</span>
              </div>
              <div className={styles.footerItem}>
                <Phone size={14} className={styles.icon} />
                <span>{member.phone}</span>
              </div>
            </div>
          </div>
        ))}

        {/* Empty State / Add New Card */}
        <div className={styles.addCard} onClick={() => setIsDrawerOpen(true)}>
          <div className={styles.addIcon}>
            <Plus size={24} />
          </div>
          <span>Add New Member</span>
        </div>
      </div>

      <Drawer 
        isOpen={isDrawerOpen} 
        onClose={() => setIsDrawerOpen(false)} 
        title="Add Family Member"
      >
        <form className="drawerForm" onSubmit={handleSubmit}>
          <div className="formGroup">
            <label>Full Name</label>
            <input name="name" value={formData.name} onChange={handleChange} required placeholder="Enter full name" />
          </div>
          <div className="formGroup">
            <label>Relationship</label>
            <select name="relation" value={formData.relation} onChange={handleChange}>
              <option value="Spouse">Spouse</option>
              <option value="Son">Son</option>
              <option value="Daughter">Daughter</option>
              <option value="Father">Father</option>
              <option value="Mother">Mother</option>
              <option value="Sibling">Sibling</option>
              <option value="Other">Other</option>
            </select>
          </div>
          <div className="formGroup">
            <label>Age</label>
            <input type="number" name="age" value={formData.age} onChange={handleChange} required placeholder="Enter age" />
          </div>
          <div className="formGroup">
            <label>Contact Number (Optional)</label>
            <input name="phone" value={formData.phone} onChange={handleChange} placeholder="Enter phone number" />
          </div>
          
          <div className="formActions">
            <button type="button" className="btnCancel" onClick={() => setIsDrawerOpen(false)}>Cancel</button>
            <button type="submit" className="btnSubmit">Add Member</button>
          </div>
        </form>
      </Drawer>
    </div>
  );
};

export default MyFamily;
